import React from 'react';
import { assignProps, animate, createViewConfig, usePortalTokenStyles, dynamic } from '@sail/engine';
import ReactDOM from 'react-dom';
import { FocusScope } from '@sail/global-state';
import { setupFocus } from '@sail/react-aria';
import { view as viewClassic } from '@sail/theme-classic';
import { themeGlobals, useTokenContext } from '@sail/engine/deprecated';
import { useInteractAnywhere } from '../hooks/useInteractAnywhere';
import { css } from '../css';
import { useFocusContainment } from './FocusContainmentProvider';
import { generateUUID, getRepositionValues, getScrollParent } from './utils';
import { useScrollbarStyleOverrides } from './IframeScrollbarStyleProvider';
import { useUiConfig } from '../providers/UiConfigProvider';
/**
 * This component is used in IframeOverrides for Popovers and Tooltips.
 * It will call the `handler`, i.e. `onClose` function when clicking on any UI layer.
 */ export const OutsideUILayerInteractions = ({ handler, children, uiLayers })=>{
    // We have to recurse to be able to iterate the hook
    const [uiLayer, ...rest] = uiLayers;
    useInteractAnywhere(handler, uiLayer === null || uiLayer === void 0 ? void 0 : uiLayer.document);
    if (rest.length > 0) {
        return /*#__PURE__*/ React.createElement(OutsideUILayerInteractions, {
            handler: handler,
            uiLayers: rest
        }, children);
    } else {
        return /*#__PURE__*/ React.createElement(React.Fragment, null, children);
    }
};
export const LayerStylesConfig = createViewConfig({
    context: false,
    element: true,
    props: {}
});
const LayerStyles = LayerStylesConfig.createView(// eslint-disable-next-line @typescript-eslint/no-explicit-any
({ children, ...props })=>{
    const { styles } = useTokenContext();
    return(// We still need to include themeGlobals, because some components may still use the Sail stable API themes.
    // Because themeGlobals has directives that are only valid on Sail's stable version, we can't
    // include it in the Sail view.div
    /*#__PURE__*/ React.createElement(viewClassic.div, {
        ...props,
        uses: [
            themeGlobals,
            styles
        ]
    }, children));
}, {
    uses: dynamic(()=>[
            usePortalTokenStyles()
        ])
});
const OverlayAnimator = ({ children })=>{
    // Ensure that the dialog transitions are restarted within the content of the iframe. Because
    // we need to wait for the iframe to be created, the content isn't rendered until after the
    // Layer is loaded, and transitions don't otherwise fire at the correct time.
    const timeline = animate.useCurrentTimeline();
    React.useEffect(()=>{
        timeline.start('in');
    }, [
        timeline
    ]);
    return /*#__PURE__*/ React.createElement(React.Fragment, null, children);
};
const useIframe = (type, initialReposition, open)=>{
    const uiConfig = useUiConfig();
    // We cannot hit this code unless requestIframe is provided
    const requestIframe = uiConfig === null || uiConfig === void 0 ? void 0 : uiConfig._unstable_requestIframe;
    const [iframeInfo, setIframeInfo] = React.useState(null);
    const isDestroyed = React.useRef(false);
    const isMounted = React.useRef(true);
    const shouldBeOpen = open == null || open === true;
    React.useEffect(()=>{
        (async ()=>{
            // When the open prop is supported, the component will always be mounted whether the layer content is shown or not.
            // Therefore, we should only request an iframe if the open prop is true or undefined, and if the open prop is set
            // to false, we should destroy the iframe if it exists since we cannot rely on the component being unmounted to
            // destroy the iframe.
            if (shouldBeOpen) {
                const iframeId = generateUUID();
                const { iframe, uiLayerMessagePort } = await requestIframe(iframeId, type, initialReposition);
                const newRoot = iframe.document.getElementById('root');
                if (newRoot && isMounted.current) {
                    setupFocus(newRoot);
                    setIframeInfo({
                        iframeId,
                        root: newRoot,
                        uiLayerMessagePort
                    });
                } else {
                    // There is an edge case where the iframe is created but destroyed almost immediately,
                    // e.g. mouseenter and mouseleave on a tooltip
                    // For such cases, the React state may not be set yet, so we need to delete the iframe via
                    // this async function. We also check the isMounted state to prevent memory leaks.
                    isDestroyed.current = true;
                    uiLayerMessagePort.deleteAccessoryLayer({
                        id: iframeId
                    });
                    setIframeInfo(null);
                }
            } else if (!isDestroyed.current && iframeInfo) {
                isDestroyed.current = true;
                iframeInfo.uiLayerMessagePort.deleteAccessoryLayer({
                    id: iframeInfo.iframeId
                });
                setIframeInfo(null);
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        shouldBeOpen,
        requestIframe,
        type
    ]);
    React.useEffect(()=>{
        if (open === true) {
            // Reset isDestroyed and isMounted
            isDestroyed.current = false;
            isMounted.current = true;
        }
    }, [
        open
    ]);
    React.useEffect(()=>()=>{
            if (!isDestroyed.current && iframeInfo) {
                isDestroyed.current = true;
                iframeInfo.uiLayerMessagePort.deleteAccessoryLayer({
                    id: iframeInfo.iframeId
                });
            }
            isMounted.current = false;
        }, [
        iframeInfo
    ]);
    return iframeInfo;
};
const usePartialIframePortal = ({ popoverRef, targetRef, overlayRef, children, open, placement = 'bottom', offset = 0, flip = true, // eslint-disable-next-line @typescript-eslint/no-empty-function
onClose = ()=>{}, containerStyles = [], inherits, uiLayers })=>{
    const { contain } = useFocusContainment();
    const normalizedOffset = React.useMemo(()=>{
        if (typeof offset === 'number') {
            return {
                mainAxis: offset,
                crossAxis: 0
            };
        }
        if (typeof offset === 'object' && offset !== null) {
            var _offset_mainAxis, _offset_crossAxis;
            return {
                mainAxis: (_offset_mainAxis = offset.mainAxis) !== null && _offset_mainAxis !== void 0 ? _offset_mainAxis : 0,
                crossAxis: (_offset_crossAxis = offset.crossAxis) !== null && _offset_crossAxis !== void 0 ? _offset_crossAxis : 0
            };
        }
        return {
            mainAxis: 0,
            crossAxis: 0
        };
    }, [
        offset
    ]);
    // For backward compatibility, use mainAxis as the margin
    const margin = `${normalizedOffset.mainAxis}px`;
    const ScrollbarStyleOverrides = useScrollbarStyleOverrides();
    // When requesting the creation of the accessory frame, we can calculate it's
    // approximate position in the document before rendering any UI.
    // This prevents issues, such as those in #ir-atmosphere-pasture, where the
    // accessory frame was positioned off screen on the first render, resulting in
    // the IntersectionObserver closing the accessory frame immediately.
    const initialRepositionValues = targetRef.current ? getRepositionValues({
        sourceElement: targetRef.current,
        margin,
        placement,
        flip
    }) : undefined;
    const ref = React.useRef();
    var _useIframe;
    const { iframeId, uiLayerMessagePort, root } = (_useIframe = useIframe('partial', initialRepositionValues, open)) !== null && _useIframe !== void 0 ? _useIframe : {};
    const onScroll = React.useCallback(()=>{
        var // We have to dispatch a synthetic blur event to hide the tooltip, since a
        // regular blur won't work if the targetRef wasn't focused to begin with
        _targetRef_current;
        (_targetRef_current = targetRef.current) === null || _targetRef_current === void 0 ? void 0 : _targetRef_current.dispatchEvent(new CustomEvent('blur'));
    }, [
        targetRef
    ]);
    const onReposition = React.useCallback(()=>{
        if (uiLayerMessagePort && iframeId && (targetRef === null || targetRef === void 0 ? void 0 : targetRef.current) && (root === null || root === void 0 ? void 0 : root.firstElementChild) && ref.current) {
            var _popoverRef_current;
            const repositionValues = getRepositionValues({
                sourceElement: targetRef.current,
                margin,
                placement,
                flip
            });
            // This block of code grabs the relevant styles we want to sync between the LayerStyles element and the iframe.
            const iframeStyles = {};
            const layerElement = ref.current;
            // We temporarily remove the inline styles on the LayerStyles element so that the Sail Next class styles can take effect
            layerElement.style.boxShadow = '';
            layerElement.style.border = '';
            layerElement.style.borderRadius = '';
            layerElement.style.marginLeft = '';
            layerElement.style.marginTop = '';
            layerElement.style.marginBottom = '';
            layerElement.style.marginRight = '';
            // We grab the computed styles from the LayerStyles element and apply them to the iframe
            // eslint-disable-next-line @sail/ui-no-reflow
            const computedStyles = window.getComputedStyle(layerElement);
            iframeStyles.boxShadow = computedStyles.boxShadow;
            iframeStyles.border = computedStyles.border;
            iframeStyles.borderRadius = computedStyles.borderRadius;
            iframeStyles.marginLeft = computedStyles.marginLeft;
            iframeStyles.marginTop = computedStyles.marginTop;
            iframeStyles.marginBottom = computedStyles.marginBottom;
            iframeStyles.marginRight = computedStyles.marginRight;
            // We reapply the inline styles on the LayerStyles element to disable them.
            layerElement.style.boxShadow = 'none';
            layerElement.style.border = 'none';
            layerElement.style.borderRadius = '0px';
            layerElement.style.marginLeft = '0px';
            layerElement.style.marginTop = '0px';
            layerElement.style.marginBottom = '0px';
            layerElement.style.marginRight = '0px';
            // We grab the computed styles from the Sail's popover element and apply them to the iframe
            // Sail's popover component handles setting the width of the popover to match the width of
            // the trigger element, so where this value is set, we want to sync it to the iframe.
            const popoverStyles = popoverRef === null || popoverRef === void 0 ? void 0 : (_popoverRef_current = popoverRef.current) === null || _popoverRef_current === void 0 ? void 0 : _popoverRef_current.style;
            if (popoverStyles === null || popoverStyles === void 0 ? void 0 : popoverStyles.width) {
                layerElement.style.width = popoverStyles.width;
            }
            uiLayerMessagePort.readjustIframe({
                id: iframeId,
                iframeStyles,
                resize: {
                    styles: {
                        // eslint-disable-next-line @sail/ui-no-reflow
                        width: getComputedStyle(root === null || root === void 0 ? void 0 : root.firstElementChild).width,
                        // eslint-disable-next-line @sail/ui-no-reflow
                        height: getComputedStyle(root === null || root === void 0 ? void 0 : root.firstElementChild).height
                    }
                },
                reposition: repositionValues
            });
        }
    }, [
        iframeId,
        margin,
        placement,
        uiLayerMessagePort,
        root === null || root === void 0 ? void 0 : root.firstElementChild,
        flip,
        targetRef,
        popoverRef
    ]);
    React.useLayoutEffect(()=>{
        uiLayerMessagePort === null || uiLayerMessagePort === void 0 ? void 0 : uiLayerMessagePort.registerObserver('interactoutside', onClose);
        uiLayerMessagePort === null || uiLayerMessagePort === void 0 ? void 0 : uiLayerMessagePort.registerObserver('scrolloutside', onScroll);
        uiLayerMessagePort === null || uiLayerMessagePort === void 0 ? void 0 : uiLayerMessagePort.registerObserver('scrolloutside', onReposition);
        const sourceElement = targetRef.current;
        if (sourceElement) {
            var _targetRef_current_ownerDocument_defaultView;
            (_targetRef_current_ownerDocument_defaultView = targetRef.current.ownerDocument.defaultView) === null || _targetRef_current_ownerDocument_defaultView === void 0 ? void 0 : _targetRef_current_ownerDocument_defaultView.addEventListener('scroll', onReposition, // Capturing the event at the window level lets us reposition the tooltip
            // on any child scroll, which prevents us from needing to attach a scroll
            // event to every scrollable ancestor in the DOM.
            {
                capture: true
            });
        }
        return ()=>{
            uiLayerMessagePort === null || uiLayerMessagePort === void 0 ? void 0 : uiLayerMessagePort.unregisterObserver('interactoutside', onClose);
            uiLayerMessagePort === null || uiLayerMessagePort === void 0 ? void 0 : uiLayerMessagePort.unregisterObserver('scrolloutside', onScroll);
            uiLayerMessagePort === null || uiLayerMessagePort === void 0 ? void 0 : uiLayerMessagePort.unregisterObserver('scrolloutside', onReposition);
            if (sourceElement) {
                var _sourceElement_ownerDocument_defaultView;
                (_sourceElement_ownerDocument_defaultView = sourceElement.ownerDocument.defaultView) === null || _sourceElement_ownerDocument_defaultView === void 0 ? void 0 : _sourceElement_ownerDocument_defaultView.removeEventListener('scroll', onReposition, {
                    capture: true
                });
            }
        };
    }, [
        onClose,
        onReposition,
        onScroll,
        targetRef,
        uiLayerMessagePort
    ]);
    // This hook will reposition the target iframe when the source element's window is resized
    React.useLayoutEffect(()=>{
        const currentOnReposition = onReposition;
        const sourceElement = targetRef.current;
        if (sourceElement) {
            var _sourceElement_ownerDocument_defaultView;
            onReposition();
            (_sourceElement_ownerDocument_defaultView = sourceElement.ownerDocument.defaultView) === null || _sourceElement_ownerDocument_defaultView === void 0 ? void 0 : _sourceElement_ownerDocument_defaultView.addEventListener('resize', currentOnReposition);
        }
        if (root) {
            var _root_ownerDocument_defaultView;
            onReposition();
            (_root_ownerDocument_defaultView = root.ownerDocument.defaultView) === null || _root_ownerDocument_defaultView === void 0 ? void 0 : _root_ownerDocument_defaultView.addEventListener('resize', currentOnReposition);
        }
        return ()=>{
            if (sourceElement) {
                var _sourceElement_ownerDocument_defaultView;
                (_sourceElement_ownerDocument_defaultView = sourceElement.ownerDocument.defaultView) === null || _sourceElement_ownerDocument_defaultView === void 0 ? void 0 : _sourceElement_ownerDocument_defaultView.removeEventListener('resize', currentOnReposition);
            }
            if (root) {
                var _root_ownerDocument_defaultView;
                (_root_ownerDocument_defaultView = root.ownerDocument.defaultView) === null || _root_ownerDocument_defaultView === void 0 ? void 0 : _root_ownerDocument_defaultView.removeEventListener('resize', currentOnReposition);
            }
        };
    }, [
        onReposition,
        root,
        targetRef
    ]);
    // This is a workaround for the React aria usePress hook.
    // usePress works by listening to the keydown event on the document, then adding a keyup event listener on the same document
    // When we open a new iframe and focus is moved to the new iframe, the keyup event is captured by the new iframe instead
    // of the original document. This causes the usePress hook to not work as expected.
    // To fix this, we manually dispatch a keyup event on the targetRef when the keyup event is captured by the new iframe.
    React.useEffect(()=>{
        const dispatchKeyupEvent = (event)=>{
            if (targetRef.current) {
                targetRef.current.dispatchEvent(new KeyboardEvent('keyup', event));
            }
        };
        if (root) {
            root.ownerDocument.addEventListener('keyup', dispatchKeyupEvent);
        }
        return ()=>{
            if (root) {
                root.ownerDocument.removeEventListener('keyup', dispatchKeyupEvent);
            }
        };
    }, [
        root,
        targetRef
    ]);
    // This enables the intersection observer for Popovers and Tooltips
    // See https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
    // This allows us to close the Popover and Tooltip when they are out of view from the viewport of the iframe
    React.useLayoutEffect(()=>{
        if (targetRef.current) {
            var _targetRef_current;
            // If scrollParent is null, we default to the browser viewport
            const scrollParent = getScrollParent(targetRef.current) || undefined;
            // We use the owner window of the target element to ensure that the default browser viewport is the target element's window
            const windowObject = ((_targetRef_current = targetRef.current) === null || _targetRef_current === void 0 ? void 0 : _targetRef_current.ownerDocument.defaultView) || window;
            const observer = new windowObject.IntersectionObserver((entries)=>{
                const entry = entries[0];
                if (entry.intersectionRatio < 0.9) {
                    // This tells us the target element is < 90% in view of the root, i.e. scrolling outside of the visible area of the root.
                    onClose();
                }
            }, {
                root: scrollParent,
                threshold: 0.9
            });
            observer.observe(targetRef.current);
            return ()=>observer.disconnect();
        }
    }, [
        onClose,
        root,
        targetRef
    ]);
    if (root) {
        return /*#__PURE__*/ ReactDOM.createPortal(/*#__PURE__*/ React.createElement(LayerStyles, {
            "data-testid": "partial-layer",
            css: [
                css({
                    width: 'fit',
                    height: 'fit'
                }),
                ...containerStyles
            ],
            inherits: inherits,
            style: {
                // These are inline style attributes that we want to apply to the iframe element instead of its content to avoid any clipping
                border: 'none',
                boxShadow: 'none',
                borderRadius: '0px',
                marginLeft: '0px',
                marginTop: '0px',
                marginBottom: '0px',
                marginRight: '0px'
            },
            ref: ref,
            uses: [
                assignProps({
                    ref: overlayRef
                })
            ],
            // Sail Popover handles this internally, but since we're not rendering within Sail's popover,
            // events will not bubble up to the Popover element and we need to handle this ourselves.
            onKeyDown: (e)=>{
                if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    onClose === null || onClose === void 0 ? void 0 : onClose();
                }
            }
        }, /*#__PURE__*/ React.createElement(FocusScope, {
            restoreFocus: contain
        }, /*#__PURE__*/ React.createElement(OutsideUILayerInteractions, {
            handler: onClose,
            uiLayers: uiLayers
        }, /*#__PURE__*/ React.createElement(ScrollbarStyleOverrides, null, children)))), root);
    }
    return /*#__PURE__*/ React.createElement("div", {
        style: {
            display: 'none'
        }
    }, children);
};
// TODO: Need to handle onClose prop when platform sends us a message to close
export const usePartialIframe = (props)=>{
    const ref = React.useRef(null);
    const portal = usePartialIframePortal({
        ...props,
        popoverRef: ref
    });
    return {
        ref: (el)=>{
            var _el_parentElement;
            // need a ref to sail's popover element to grab styles to apply to the iframe
            // but due to the `inherits` usage on LayerStyles, the ref is bound to multiple elements
            // (once to Sail's popover, and once to the iframe layer element)
            if (el && ((_el_parentElement = el.parentElement) === null || _el_parentElement === void 0 ? void 0 : _el_parentElement.id) !== 'root') {
                ref.current = el.parentElement;
            }
        },
        style: {
            display: 'none'
        },
        children: /*#__PURE__*/ React.createElement(React.Fragment, null, portal)
    };
};
export const useOverlayIframe = ({ children })=>{
    var _useIframe;
    const { root } = (_useIframe = useIframe('overlay')) !== null && _useIframe !== void 0 ? _useIframe : {};
    const { contain } = useFocusContainment();
    const uiConfig = useUiConfig();
    const ScrollbarStyleOverrides = useScrollbarStyleOverrides();
    // Due to a bug with react-aria, when platform iframes wrap an open overlay component, it may block other popover components on the platform frame
    // We selectively disable focus containment, like when rendering the mobile webview in the docs site
    const skipFocusContainment = uiConfig === null || uiConfig === void 0 ? void 0 : uiConfig._unstable_skipFocusContainment;
    const focusContainment = skipFocusContainment ? false : contain;
    return {
        style: {
            display: 'none'
        },
        children: root ? /*#__PURE__*/ ReactDOM.createPortal(/*#__PURE__*/ React.createElement(OverlayAnimator, null, /*#__PURE__*/ React.createElement(LayerStyles, {
            "data-testid": "overlay-layer",
            css: {
                width: 'fit',
                height: 'fit'
            }
        }, /*#__PURE__*/ React.createElement(FocusScope, {
            contain: focusContainment,
            autoFocus: focusContainment,
            restoreFocus: focusContainment
        }, /*#__PURE__*/ React.createElement(ScrollbarStyleOverrides, null, children)))), root) : null
    };
};

//# sourceMappingURL=OverlayOverrides.js.map